import { useId, useMemo } from "react";

import { cn } from "@/lib/utils";

interface ClusterIconProps extends React.ComponentProps<"svg"> {
  state?: "active" | "inactive" | "normal";
}

interface PinIconProps extends React.ComponentProps<"svg"> {
  state?: "inactive" | "normal";
}

const PIN_STATE_COLORS = {
  inactive: ["#737392", "#9FA0A0", "#9FA0A0"],
  normal: ["#7E1F7C", "#BD2885", "#90278E"],
};

export function CompletedIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("size-11.5", className)}
      fill="none"
      viewBox="0 0 46 46"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <rect
          fill={`url(#${id}b)`}
          height="26"
          rx="13"
          width="26"
          x="10"
          y="10"
        />
        <path
          clipRule="evenodd"
          d="M20.454 26.061l-2.744-2.949a.815.815 0 00-1.193 1.111l3.938 4.232 9.028-9.703a.815.815 0 00-1.194-1.11l-7.835 8.42z"
          fill="#fff"
          fillRule="evenodd"
          stroke="#fff"
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="26.982"
          x2="7.802"
          y1="0.41"
          y2="18.445"
        >
          <stop stopColor="#96deda" />
          <stop offset="1" stopColor="#1cd8d2" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="46"
          id={`${id}a`}
          width="46"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix values="0 0 0 0 0.313726 0 0 0 0 0.788235 0 0 0 0 0.764706 0 0 0 0.8 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_9902_30384"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_9902_30384"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function CompletedActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("size-11.5", className)}
      fill="none"
      viewBox="0 0 46 46"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <rect
          fill={`url(#${id}b)`}
          height="26"
          rx="13"
          width="26"
          x="10"
          y="10"
        />
        <path
          clipRule="evenodd"
          d="M20.454 26.061l-2.744-2.949a.815.815 0 00-1.193 1.111l3.938 4.232 9.028-9.703a.815.815 0 00-1.194-1.11l-7.835 8.42z"
          fill="#fff"
          fillRule="evenodd"
          stroke="#fff"
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="16.5"
          x2="3.5"
          y1="16.5"
          y2="29.5"
        >
          <stop stopColor="#ffc371" />
          <stop offset="1" stopColor="#ff5f6d" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="46"
          id={`${id}a`}
          width="46"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="5" />
          <feColorMatrix values="0 0 0 0 1 0 0 0 0 0.372549 0 0 0 0 0.427451 0 0 0 1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_0_1257"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_0_1257"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function ClusterIcon({
  className,
  state = "normal",
  ...props
}: ClusterIconProps) {
  const id = useId();
  const [startColor, endColor] = useMemo(() => {
    const gradientColors = {
      active: ["#FFC371", "#FF5F6D"],
      inactive: ["#9FA0A0", "#9FA0A0"],
      normal: ["#BD2885", "#90278E"],
    };
    return gradientColors[state];
  }, [state]);

  return (
    <svg
      aria-hidden="true"
      className={cn("size-6.5", className)}
      fill="none"
      viewBox="0 0 26 26"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect fill={`url(#${id}a)`} height="26" rx="13" width="26" />
      <rect fill={`url(#${id}b)`} height="26" rx="13" width="26" />
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}a`}
          x1="16.982"
          x2="-2.198"
          y1="-9.59"
          y2="8.445"
        >
          <stop stopColor="#1B1B27" />
          <stop offset="1" stopColor="#1B1B27" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="6.5"
          x2="-6.5"
          y1="6.5"
          y2="19.5"
        >
          <stop stopColor={startColor} />
          <stop offset="1" stopColor={endColor} />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function EscapesActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-17.5 w-14", className)}
      fill="none"
      viewBox="0 0 56 70"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M28 14c-7.732 0-14 6.268-14 14 0 2.688.758 5.2 2.071 7.332L28 56l11.998-20.78A13.935 13.935 0 0042 28c0-7.732-6.268-14-14-14z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
        <g filter={`url(#${id}c)`}>
          <path
            clipRule="evenodd"
            d="M32.622 26.77c-.61 1.012-2.279 1.157-3.945.426l-.604 1.003-1.256 2.086 1.49.895a.375.375 0 01.13.515l-.212.35a.375.375 0 01-.514.129l-.277-.167a.375.375 0 00-.515.129l-.313.522a.375.375 0 00.128.514l.277.167a.375.375 0 01.128.514l-.17.285a.375.375 0 01-.515.128l-1.494-.897-.264.438a.818.818 0 01-1.4-.843l1.807-3.003 1.57-2.606.603-1.002c-1.43-1.13-2.085-2.678-1.475-3.69.699-1.161 2.792-1.182 4.676-.048 1.884 1.134 2.844 2.995 2.145 4.155zm-2.505-3.527c.456.275.69.769.63 1.234.229.001.465.064.681.194.6.362.816 1.104.482 1.659-.334.554-1.091.71-1.691.35a1.34 1.34 0 01-.491-.512c-.383.27-.93.295-1.385.02-.456-.274-.69-.768-.63-1.233a1.339 1.339 0 01-.681-.195c-.6-.361-.816-1.104-.482-1.658.334-.555 1.09-.711 1.69-.35.217.13.383.31.492.512.383-.271.929-.295 1.385-.02z"
            fill="#E0903D"
            fillRule="evenodd"
          />
        </g>
        <path
          d="M28.677 27.196l.15-.343a.375.375 0 00-.471.15l.321.193zm3.945-.426l-.321-.194.321.194zm-4.549 1.429l.321.193-.32-.193zm-1.256 2.086l-.321-.194a.375.375 0 00.128.515l.193-.321zm1.49.895l.194-.321-.193.321zm.13.515l.32.193-.32-.193zm-.212.35l-.32-.193.32.193zm-.514.129l-.193.321.193-.321zm-.277-.167l.193-.321-.193.321zm-.515.129l.322.193-.322-.193zm-.313.522l-.322-.193.322.193zm.128.514l-.193.322.193-.322zm.277.167l.193-.322-.193.322zm.128.514l.322.193-.322-.193zm-.17.285l-.322-.193.321.193zm-.515.128l-.194.322.194-.322zm-1.494-.897l.193-.322a.375.375 0 00-.514.128l.32.194zm-.264.438l-.322-.193.322.193zm-1.122.279l-.194.32.194-.32zm-.279-1.122l-.321-.194.321.194zm1.808-3.003l-.321-.194.321.194zm1.57-2.606l.32.194-.32-.194zm.603-1.002l.321.194a.375.375 0 00-.089-.488l-.232.294zm-1.475-3.69l.321.193-.32-.194zm4.676-.048l.193-.321-.193.321zm.27 1.862l-.372-.048a.375.375 0 00.369.423l.003-.375zm-.63-1.234l.193-.321-.193.321zm1.311 1.428l.194-.32-.194.32zm.482 1.659l.321.193-.321-.193zm-1.691.35l.193-.322-.193.322zm-.491-.512l.33-.178a.375.375 0 00-.546-.128l.216.306zm-1.385.02l-.193.322.193-.322zm-.63-1.233l.372.048a.375.375 0 00-.37-.423l-.002.375zm-.681-.195l-.194.321.194-.32zm-.482-1.658l-.322-.194.322.194zm1.69-.35l.194-.321-.193.321zm.492.512l-.33.177a.375.375 0 00.546.129l-.216-.306zm-.206 4.276c.88.386 1.78.55 2.558.47.777-.08 1.475-.408 1.86-1.047l-.643-.387c-.225.374-.666.624-1.293.688-.625.063-1.394-.066-2.18-.411l-.302.687zm-.132.852l.604-1.002-.642-.387-.604 1.002.642.387zm-1.256 2.086l1.256-2.086-.642-.387-1.256 2.086.642.387zm1.363.38l-1.49-.895-.387.643 1.49.896.387-.643zm.256 1.03a.75.75 0 00-.256-1.029l-.386.643.643.386zm-.21.35l.21-.35-.642-.386-.21.35.642.387zm-1.03.257a.75.75 0 001.03-.256l-.643-.387-.386.643zm-.276-.166l.277.166.386-.643-.277-.166-.386.643zm0 0l.386-.643a.75.75 0 00-1.029.256l.643.387zm-.314.522l.314-.522-.643-.387-.314.523.643.386zm0 0l-.643-.386a.75.75 0 00.257 1.029l.386-.643zm.277.166l-.277-.166-.386.643.277.166.386-.643zm.257 1.03a.75.75 0 00-.257-1.03l-.386.643.643.386zm-.171.284l.17-.285-.642-.386-.171.285.643.386zm-1.03.257a.75.75 0 001.03-.257l-.643-.386-.386.643zm-1.493-.898l1.494.898.386-.643-1.494-.898-.386.643zm.25.31l.264-.438-.642-.387-.264.439.642.387zm-1.637.407c.564.34 1.297.158 1.637-.406l-.642-.387a.443.443 0 01-.608.15l-.387.643zm-.406-1.637a1.193 1.193 0 00.406 1.637l.387-.643a.443.443 0 01-.15-.607l-.643-.387zm1.808-3.003l-1.808 3.003.642.387 1.808-3.003-.642-.387zm1.57-2.605l-1.57 2.605.642.387 1.57-2.605-.643-.387zm.602-1.002l-.603 1.002.643.387.603-1.002-.643-.387zm-1.474-3.691c-.385.64-.349 1.411-.054 2.136.295.726.862 1.445 1.617 2.043l.465-.589c-.674-.534-1.15-1.153-1.387-1.737-.238-.584-.224-1.093.001-1.466l-.642-.387zm5.19-.175c-.991-.597-2.053-.9-2.979-.891-.92.01-1.77.332-2.211 1.066l.642.387c.258-.428.803-.695 1.576-.703.767-.008 1.693.246 2.585.783l.387-.642zm2.273 4.67c.442-.734.33-1.637-.092-2.453-.425-.823-1.19-1.62-2.18-2.217l-.387.642c.892.538 1.55 1.238 1.9 1.919.355.687.374 1.294.117 1.721l.642.387zm-1.825-2.439c.08-.62-.231-1.256-.808-1.603l-.387.642c.336.202.492.555.452.865l.743.096zm.504-.175a1.714 1.714 0 00-.873-.248l-.005.75a.965.965 0 01.49.14l.388-.642zm.61 2.173c.454-.755.143-1.72-.61-2.173l-.387.643c.447.269.567.79.354 1.143l.642.387zm-2.207.478c.754.453 1.751.278 2.206-.478l-.642-.387c-.213.354-.73.492-1.177.222l-.387.643zm-.627-.655c.14.26.353.49.627.655l.387-.643a.964.964 0 01-.354-.368l-.66.356zm-1.248.164c.576.347 1.284.325 1.795-.036l-.433-.612c-.255.18-.64.207-.976.005l-.386.643zm-.809-1.604c-.08.62.232 1.257.809 1.604l.387-.643c-.336-.202-.492-.555-.452-.864l-.744-.096zm-.503.175c.274.165.576.247.873.249l.005-.75a.964.964 0 01-.491-.141l-.387.642zm-.61-2.173c-.454.756-.143 1.72.61 2.173l.387-.642c-.447-.27-.567-.79-.354-1.144l-.642-.387zm2.206-.477c-.753-.454-1.75-.278-2.206.477l.643.387c.213-.354.73-.491 1.177-.222l.386-.642zm.628.655a1.714 1.714 0 00-.628-.655l-.387.642a.964.964 0 01.354.368l.661-.355zm1.248-.164c-.576-.347-1.284-.326-1.795.035l.433.613c.255-.18.64-.207.975-.006l.387-.642z"
          fill="#E0903D"
        />
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="70"
          id={`${id}a`}
          width="56"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 1 0 0 0 0 0.372549 0 0 0 0 0.427451 0 0 0 1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_15396_130665"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_15396_130665"
            result="shape"
          />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="13.292"
          id={`${id}c`}
          width="10.423"
          x="22.803"
          y="21.403"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130665" />
        </filter>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="21"
          x2="1.615"
          y1="24.5"
          y2="37.423"
        >
          <stop stopColor="#FFC371" />
          <stop offset="1" stopColor="#FF5F6D" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function EscapesIcon({
  className,
  state = "normal",
  ...props
}: PinIconProps) {
  const id = useId();
  const [fill, startColor, endColor] = useMemo(
    () => PIN_STATE_COLORS[state],
    [state]
  );

  return (
    <svg
      aria-hidden="true"
      className={cn("h-10.5 w-7", className)}
      fill="none"
      viewBox="0 0 28 42"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M14 0C6.268 0 0 6.268 0 14c0 2.688.758 5.2 2.071 7.332L14 42l11.998-20.78A13.935 13.935 0 0028 14c0-7.732-6.268-14-14-14z"
        fill={`url(#${id}a)`}
        fillRule="evenodd"
      />
      <g filter={`url(#${id}b)`}>
        <path
          clipRule="evenodd"
          d="M18.622 12.77c-.61 1.012-2.279 1.157-3.945.426l-.604 1.003-1.256 2.086 1.49.895a.375.375 0 01.13.515l-.211.35a.375.375 0 01-.515.129l-.277-.167a.375.375 0 00-.514.129l-.314.522a.375.375 0 00.128.514l.277.167a.375.375 0 01.128.514l-.17.285a.375.375 0 01-.515.128l-1.494-.897-.264.438a.818.818 0 01-1.4-.843l1.807-3.003 1.57-2.606.603-1.002c-1.43-1.13-2.085-2.678-1.475-3.69.699-1.161 2.792-1.182 4.676-.048 1.883 1.134 2.844 2.995 2.145 4.155zm-2.505-3.527c.456.275.69.769.63 1.234.229.001.465.064.681.194.6.362.816 1.104.482 1.659-.334.554-1.091.71-1.691.35a1.34 1.34 0 01-.491-.512c-.383.27-.93.295-1.385.02-.456-.274-.69-.769-.63-1.233a1.339 1.339 0 01-.681-.195c-.6-.361-.816-1.104-.482-1.658.334-.555 1.09-.711 1.69-.35.217.13.383.31.492.512.383-.271.929-.295 1.385-.02z"
          fill={fill}
          fillRule="evenodd"
        />
      </g>
      <path
        d="M14.677 13.196l.15-.343a.375.375 0 00-.471.15l.321.193zm3.945-.426l-.321-.194.321.194zm-4.549 1.429l.321.193-.32-.193zm-1.256 2.086l-.321-.194a.375.375 0 00.128.515l.193-.321zm1.49.895l.194-.321-.193.321zm.13.515l.32.193-.32-.193zm-.211.35l-.322-.193.322.193zm-.515.129l-.193.321.193-.321zm-.277-.167l.193-.321-.193.321zm-.514.129l.321.193-.322-.193zm-.314.522l-.322-.193.322.193zm.128.514l-.193.322.193-.322zm.277.167l.193-.322-.193.322zm.128.514l.322.193-.322-.193zm-.17.285l-.322-.193.321.193zm-.515.128l-.194.322.194-.322zm-1.494-.897l.193-.322a.375.375 0 00-.514.128l.32.194zm-.264.438l-.322-.193.322.193zm-1.122.279l-.194.32.194-.32zm-.279-1.122l-.321-.194.321.194zm1.808-3.003l-.321-.194.321.194zm1.57-2.606l.32.194-.32-.194zm.603-1.002l.321.194a.375.375 0 00-.089-.488l-.232.295zM11.8 8.664l.321.193-.32-.194zm4.676-.048l.193-.321-.193.321zm.27 1.862l-.372-.048a.375.375 0 00.369.423l.003-.375zm-.63-1.234l.193-.321-.193.321zm1.311 1.428l.194-.32-.194.32zm.482 1.659l.321.193-.321-.193zm-1.691.35l.193-.322-.193.322zm-.491-.512l.33-.178a.375.375 0 00-.546-.128l.216.306zm-1.385.02l-.193.322.193-.322zm-.63-1.233l.372.048a.375.375 0 00-.37-.423l-.002.375zm-.681-.195l-.194.321.194-.32zm-.482-1.658l-.322-.194.322.194zm1.69-.35l.194-.321-.193.321zm.492.512l-.33.177a.375.375 0 00.546.129l-.216-.306zm-.206 4.276c.88.386 1.78.55 2.558.47.777-.08 1.475-.408 1.86-1.047l-.643-.386c-.225.373-.666.623-1.293.687-.625.063-1.394-.066-2.18-.411l-.302.687zm-.132.852l.604-1.002-.642-.387-.604 1.002.642.387zm-1.256 2.086l1.256-2.086-.642-.387-1.256 2.086.642.387zm1.363.38l-1.49-.895-.387.643 1.49.896.387-.643zm.257 1.03a.75.75 0 00-.257-1.029l-.386.643.643.386zm-.211.35l.21-.35-.642-.386-.21.35.642.386zm-1.03.257a.75.75 0 001.03-.257l-.643-.386-.386.643zm-.276-.166l.277.166.386-.643-.277-.166-.386.643zm0 0l.386-.643a.75.75 0 00-1.029.256l.643.387zm-.314.522l.314-.522-.643-.387-.314.523.643.386zm0 0l-.643-.386a.75.75 0 00.257 1.029l.386-.643zm.277.166l-.277-.166-.386.643.277.166.386-.643zm.257 1.03a.75.75 0 00-.257-1.03l-.386.643.643.386zm-.171.284l.17-.285-.642-.386-.171.285.643.386zm-1.03.257a.75.75 0 001.03-.257l-.643-.386-.386.643zm-1.493-.898l1.494.898.386-.643-1.494-.898-.386.643zm.25.31l.264-.438-.642-.387-.264.439.642.386zm-1.637.407c.564.34 1.297.158 1.637-.407l-.643-.386a.443.443 0 01-.607.15l-.387.643zm-.406-1.637a1.192 1.192 0 00.406 1.637l.387-.643a.443.443 0 01-.15-.607l-.643-.387zm1.808-3.002L8.974 18.77l.642.387 1.809-3.003-.643-.386zm1.57-2.606l-1.57 2.605.643.387 1.569-2.605-.643-.387zm.602-1.002l-.603 1.002.643.387.603-1.002-.643-.387zM11.48 8.469c-.385.64-.349 1.411-.054 2.136.295.726.862 1.445 1.617 2.043l.465-.589c-.674-.534-1.15-1.153-1.387-1.737-.238-.584-.224-1.093.001-1.466l-.642-.387zm5.19-.175c-.991-.597-2.053-.9-2.979-.891-.92.01-1.77.332-2.211 1.066l.642.387c.258-.428.803-.695 1.576-.703.767-.008 1.693.246 2.585.783l.387-.642zm2.273 4.67c.442-.734.33-1.637-.092-2.453-.425-.823-1.19-1.62-2.18-2.217l-.387.642c.892.537 1.55 1.238 1.9 1.919.355.687.374 1.294.117 1.721l.642.387zm-1.825-2.439c.08-.62-.231-1.256-.808-1.603l-.387.642c.336.202.492.555.452.865l.743.096zm.504-.175a1.714 1.714 0 00-.873-.248l-.005.75a.965.965 0 01.49.14l.388-.642zm.61 2.173c.454-.755.143-1.72-.61-2.173l-.387.643c.447.269.567.79.354 1.143l.642.387zm-2.207.478c.754.453 1.751.278 2.206-.478l-.642-.387c-.213.354-.73.492-1.177.222l-.387.643zm-.627-.655c.14.26.353.49.627.655l.387-.643a.964.964 0 01-.354-.368l-.66.355zm-1.248.164c.576.347 1.284.325 1.794-.036l-.432-.612c-.255.18-.64.207-.976.005l-.386.643zm-.809-1.604c-.08.62.232 1.256.809 1.604l.386-.643c-.335-.202-.49-.555-.45-.864l-.745-.097zm-.503.175c.274.165.576.247.873.249l.005-.75a.964.964 0 01-.491-.141l-.387.642zm-.61-2.173c-.454.756-.143 1.72.61 2.173l.387-.642c-.447-.27-.567-.79-.354-1.144l-.643-.387zm2.206-.477c-.753-.454-1.75-.278-2.205.477l.642.387c.213-.354.73-.491 1.176-.222l.387-.642zm.628.655a1.714 1.714 0 00-.628-.655l-.386.642a.964.964 0 01.353.368l.661-.355zm1.248-.164c-.576-.347-1.284-.326-1.795.035l.433.613c.255-.18.64-.208.975-.006l.387-.642z"
        fill={fill}
      />
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}a`}
          x1="7"
          x2="-12.385"
          y1="10.5"
          y2="23.423"
        >
          <stop stopColor={startColor} />
          <stop offset="1" stopColor={endColor} />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="13.292"
          id={`${id}b`}
          width="10.423"
          x="8.803"
          y="7.403"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130550" />
        </filter>
      </defs>
    </svg>
  );
}

export function ExperiencesActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-17.5 w-14", className)}
      fill="none"
      viewBox="0 0 56 70"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M28 14c-7.732 0-14 6.268-14 14 0 2.688.758 5.2 2.071 7.332L28 56l11.998-20.78A13.935 13.935 0 0042 28c0-7.732-6.268-14-14-14z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
        <g clipPath={`url(#${id}c)`} filter={`url(#${id}d)`}>
          <path
            clipRule="evenodd"
            d="M26.085 29.99c-.738-.577-1.293-1.21-1.6-1.752-.153-.271-.233-.499-.256-.67-.022-.168.014-.242.038-.273.024-.03.086-.083.256-.102.17-.02.41.002.712.086.6.166 1.348.55 2.087 1.126.739.576 1.293 1.21 1.6 1.751.154.272.233.499.256.67.023.169-.013.242-.037.273-.024.03-.087.084-.256.103-.171.019-.411-.003-.712-.086-.6-.166-1.349-.55-2.087-1.127zm-2.56-3.273c-.226.29-.272.645-.227.976.02.15.059.304.114.458l-.752 5.48a.509.509 0 00.688.544l5.254-2.032c.134.009.264.007.388-.007.332-.038.665-.168.892-.459.227-.29.272-.645.228-.976-.044-.329-.18-.673-.37-1.008-.381-.673-1.028-1.396-1.84-2.03-.811-.633-1.67-1.084-2.415-1.29-.371-.103-.738-.152-1.068-.114-.332.037-.665.168-.892.458z"
            fill="#E0903D"
            fillRule="evenodd"
          />
          <path
            d="M25.343 27.617c.663-.565.926-1.188.869-2.06-.04-.605-.23-1.095-.577-1.482m3.041 5.728c.365-.792.893-1.213 1.746-1.39.485-.101.924-.084 1.322.055m-4.815.386l4.009-5.52"
            stroke="#E0903D"
            strokeLinecap="round"
            strokeWidth="0.8"
          />
          <circle
            cx="22.492"
            cy="23.27"
            fill="#E0903D"
            r="0.583"
            transform="rotate(-15 22.492 23.27)"
          />
          <circle
            cx="32.604"
            cy="26.381"
            fill="#E0903D"
            r="0.583"
            transform="rotate(-15 32.604 26.381)"
          />
          <circle
            cx="33.381"
            cy="22.395"
            fill="#E0903D"
            r="0.583"
            transform="rotate(-15 33.381 22.395)"
          />
          <path
            d="M27.905 21.404a.15.15 0 01.276-.074l.297.458a.15.15 0 00.165.063l.527-.141a.15.15 0 01.155.24l-.343.424a.15.15 0 00-.01.176l.298.457a.15.15 0 01-.18.222l-.51-.195a.15.15 0 00-.17.045l-.343.424a.15.15 0 01-.267-.102l.029-.545a.15.15 0 00-.096-.148l-.51-.195a.15.15 0 01.015-.285l.528-.142a.15.15 0 00.11-.137l.03-.545zm4.135 8.341a.15.15 0 01.275-.074l.464.714a.15.15 0 00.165.063l.822-.22a.15.15 0 01.155.24l-.536.66a.15.15 0 00-.009.177l.464.714a.15.15 0 01-.18.221l-.794-.305a.15.15 0 00-.17.046l-.536.661a.15.15 0 01-.267-.102l.045-.85a.15.15 0 00-.096-.148l-.795-.305a.15.15 0 01.015-.285l.822-.22a.15.15 0 00.111-.137l.045-.85z"
            fill="#E0903D"
          />
        </g>
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="70"
          id={`${id}a`}
          width="56"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 1 0 0 0 0 0.372549 0 0 0 0 0.427451 0 0 0 1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_15396_130675"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_15396_130675"
            result="shape"
          />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="14"
          id={`${id}d`}
          width="14"
          x="21"
          y="21"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130675" />
        </filter>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="21"
          x2="1.615"
          y1="24.5"
          y2="37.423"
        >
          <stop stopColor="#FFC371" />
          <stop offset="1" stopColor="#FF5F6D" />
        </linearGradient>
        <clipPath id={`${id}c`}>
          <path d="M21 21h14v14H21z" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function ExperiencesIcon({
  className,
  state = "normal",
  ...props
}: PinIconProps) {
  const id = useId();
  const [fill, startColor, endColor] = useMemo(
    () => PIN_STATE_COLORS[state],
    [state]
  );

  return (
    <svg
      aria-hidden="true"
      className={cn("h-10.5 w-7", className)}
      fill="none"
      viewBox="0 0 28 42"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M14 0C6.268 0 0 6.268 0 14c0 2.688.758 5.2 2.071 7.332L14 42l11.998-20.78A13.935 13.935 0 0028 14c0-7.732-6.268-14-14-14z"
        fill={`url(#${id}a)`}
        fillRule="evenodd"
      />
      <g clipPath={`url(#${id}b)`} filter={`url(#${id}c)`}>
        <path
          clipRule="evenodd"
          d="M12.085 15.99c-.74-.577-1.294-1.21-1.6-1.752-.154-.271-.234-.499-.257-.67-.022-.168.014-.242.038-.273.024-.03.086-.083.255-.102.172-.02.412.002.713.086.6.166 1.348.55 2.087 1.126.739.577 1.293 1.21 1.6 1.751.154.272.233.499.256.67.023.169-.013.242-.037.273-.024.03-.087.084-.256.103-.171.019-.411-.003-.712-.086-.6-.166-1.349-.55-2.087-1.127zm-2.56-3.273c-.227.29-.273.645-.228.976.02.15.06.304.114.458l-.752 5.48a.509.509 0 00.688.544l5.254-2.032c.134.009.264.007.388-.007.332-.038.665-.168.892-.459.227-.29.272-.645.228-.976-.044-.329-.18-.673-.37-1.008-.381-.673-1.028-1.396-1.84-2.03-.811-.633-1.67-1.084-2.415-1.29-.371-.103-.738-.151-1.068-.114-.332.037-.665.168-.891.458z"
          fill={fill}
          fillRule="evenodd"
        />
        <path
          d="M11.342 13.617c.664-.565.927-1.188.87-2.06-.04-.605-.23-1.095-.577-1.482m3.04 5.728c.366-.792.894-1.213 1.746-1.39.486-.101.924-.084 1.322.055m-4.814.386l4.009-5.52"
          stroke={fill}
          strokeLinecap="round"
          strokeWidth="0.8"
        />
        <circle
          cx="8.493"
          cy="9.27"
          fill={fill}
          r="0.583"
          transform="rotate(-15 8.493 9.27)"
        />
        <circle
          cx="18.604"
          cy="12.381"
          fill={fill}
          r="0.583"
          transform="rotate(-15 18.604 12.381)"
        />
        <circle
          cx="19.381"
          cy="8.395"
          fill={fill}
          r="0.583"
          transform="rotate(-15 19.381 8.395)"
        />
        <path
          d="M13.905 7.404a.15.15 0 01.275-.074l.298.458a.15.15 0 00.164.063l.528-.141a.15.15 0 01.155.24l-.344.424a.15.15 0 00-.009.176l.298.458a.15.15 0 01-.18.221l-.51-.195a.15.15 0 00-.17.045l-.344.425A.15.15 0 0113.8 9.4l.029-.545a.15.15 0 00-.097-.148l-.51-.195a.15.15 0 01.016-.285l.527-.142a.15.15 0 00.111-.137l.029-.545zm4.135 8.341a.15.15 0 01.275-.074l.464.714a.15.15 0 00.165.063l.822-.22a.15.15 0 01.155.24l-.536.66a.15.15 0 00-.009.177l.464.713a.15.15 0 01-.18.222l-.794-.305a.15.15 0 00-.17.046l-.536.661a.15.15 0 01-.267-.102l.045-.85a.15.15 0 00-.096-.148l-.795-.305a.15.15 0 01.015-.285l.822-.22a.15.15 0 00.111-.137l.045-.85z"
          fill={fill}
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}a`}
          x1="7"
          x2="-12.385"
          y1="10.5"
          y2="23.423"
        >
          <stop stopColor={startColor} />
          <stop offset="1" stopColor={endColor} />
        </linearGradient>
        <clipPath id={`${id}b`}>
          <path d="M7 7h14v14H7z" fill="#fff" />
        </clipPath>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="14"
          id={`${id}c`}
          width="14"
          x="7"
          y="7"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130660" />
        </filter>
      </defs>
    </svg>
  );
}

export function HalloweenActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-13 w-14", className)}
      fill="none"
      viewBox="0 0 56 52"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          d="M24.795 11.783c.283.21.528.47.759.735.288.331.535.742.698 1.147.563 1.4.178 2.974-.621 4.205l.978.444a2.75 2.75 0 0 1 1.532-.579c.708-.038 1.344.244 1.67.424.136-.177.273-.33.411-.473-.726-.689-1.178-1.613-1.457-2.578-.181-.626-.282-1.272-.458-1.899a13 13 0 0 0-.365-1.126c-.42-1.097-1.154-2.092-2.439-2.083-.582.004-1.49.318-1.37 1.06.054.34.41.537.662.723"
          fill={`url(#${id}b)`}
        />
        <path
          d="M45.104 23.457c-.447-1.112-2.8-4.724-5.161-5.92-2.362-1.198-4.556-.774-5.89-.354 0 0-1.114-.224-1.816-.064-.702.158-1.583.64-2.343 1.73 0 0-.801-.564-1.73-.514s-1.5.627-1.5.627l-1.626-.74c-1.102-.512-3.587-1.173-5.84-1.076-2.254.099-4.045 1.187-5.707 2.834s-2.845 4.946-3.355 7.48c-.51 2.536.412 7.1 2.737 9.365 2.325 2.264 4.03 2.836 7.223 2.83 0 0 1.57 1.024 3.431 1.326 1.861.304 3.137.118 4.86-.433 0 0 .774.696 1.666.736.89.039 1.688-.51 2.26-1.223 0 0 .364.48 1.342.567s2.16-.488 2.632-.908c0 0 1.483.385 2.766.046 1.284-.34 2.008-.991 3.036-2.081 1.03-1.09 2.835-3.834 3.612-7.156.775-3.323-.15-5.96-.597-7.072m-11.501-2.465 3.97 4.825-5.828.864zm-10.199.224 2.12 5.641-5.787-.81zm13.966 13.49-2.264-2.383-1.708 2.934-2.01-2.428-2.45 3.046-2.201-2.834-2.644 2.419-2.018-2.24-2.872 2.088-3.714-6.715 4.07 2.268 1.99-1.797 2.6 2.183 2.508-2.286 2.408 2.39 2.001-2.235 2.08 1.953 1.498-2.066 1.828 1.115 4.386-2.65z"
          fill={`url(#${id}c)`}
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="24.283"
          x2="32.603"
          y1="18.213"
          y2="13.723"
        >
          <stop stopColor="#c83c00" />
          <stop offset="1" stopColor="#f90" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}c`}
          x1="10.947"
          x2="40.24"
          y1="40.986"
          y2="9.377"
        >
          <stop stopColor="#c83c00" />
          <stop offset="1" stopColor="#f90" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="51.286"
          id={`${id}a`}
          width="56"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.784314 0 0 0 0 0.235294 0 0 0 0 0 0 0 0 0.8 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_6890_30971"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_6890_30971"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function HalloweenIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-13 w-14", className)}
      fill="none"
      viewBox="0 0 56 52"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          d="M24.795 11.783c.283.21.528.47.759.735.288.331.535.742.698 1.147.563 1.4.178 2.974-.621 4.205l.978.444a2.75 2.75 0 0 1 1.532-.579c.708-.038 1.344.244 1.67.424.136-.177.273-.33.411-.473-.726-.689-1.178-1.613-1.457-2.578-.181-.626-.282-1.272-.458-1.899a13 13 0 0 0-.365-1.126c-.42-1.097-1.154-2.092-2.439-2.083-.582.004-1.49.318-1.37 1.06.054.34.41.537.662.723"
          fill={`url(#${id}b)`}
        />
        <path
          d="M45.104 23.457c-.447-1.112-2.8-4.724-5.161-5.92-2.362-1.198-4.556-.774-5.89-.354 0 0-1.114-.224-1.816-.064-.702.158-1.583.64-2.343 1.73 0 0-.801-.564-1.73-.514s-1.5.627-1.5.627l-1.626-.74c-1.102-.512-3.587-1.173-5.84-1.076-2.254.099-4.045 1.187-5.707 2.834s-2.845 4.946-3.355 7.48c-.51 2.536.412 7.1 2.737 9.365 2.325 2.264 4.03 2.836 7.223 2.83 0 0 1.57 1.024 3.431 1.326 1.861.304 3.137.118 4.86-.433 0 0 .774.696 1.666.736.89.039 1.688-.51 2.26-1.223 0 0 .364.48 1.342.567s2.16-.488 2.632-.908c0 0 1.483.385 2.766.046 1.284-.34 2.008-.991 3.036-2.081 1.03-1.09 2.835-3.834 3.612-7.156.775-3.323-.15-5.96-.597-7.072m-11.501-2.465 3.97 4.825-5.828.864zm-10.199.224 2.12 5.641-5.787-.81zm13.966 13.49-2.264-2.383-1.708 2.934-2.01-2.428-2.45 3.046-2.201-2.834-2.644 2.419-2.018-2.24-2.872 2.088-3.714-6.715 4.07 2.268 1.99-1.797 2.6 2.183 2.508-2.286 2.408 2.39 2.001-2.235 2.08 1.953 1.498-2.066 1.828 1.115 4.386-2.65z"
          fill={`url(#${id}c)`}
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="24.283"
          x2="32.603"
          y1="18.213"
          y2="13.723"
        >
          <stop stopColor="#c86000" />
          <stop offset="1" stopColor="#ffb800" />
        </linearGradient>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}c`}
          x1="10.947"
          x2="40.24"
          y1="40.986"
          y2="9.377"
        >
          <stop stopColor="#c86000" />
          <stop offset="1" stopColor="#ffb800" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="51.286"
          id={`${id}a`}
          width="56"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="5" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 0.784314 0 0 0 0 0.376471 0 0 0 0 0 0 0 0 0.8 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_6890_30968"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_6890_30968"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function HauntsActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-17.5 w-14", className)}
      fill="none"
      viewBox="0 0 56 70"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M28 14c-7.732 0-14 6.268-14 14 0 2.688.758 5.2 2.071 7.332L28 56l11.998-20.78A13.935 13.935 0 0042 28c0-7.732-6.268-14-14-14z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
        <g filter={`url(#${id}c)`}>
          <path
            clipRule="evenodd"
            d="M23.157 25.501a4.97 4.97 0 019.826 0L34.126 33c.158 1.038-1.052 1.718-1.857 1.044l-.46-.385a.655.655 0 00-1.002.199l-.082.158a1.213 1.213 0 01-2.129.04.607.607 0 00-1.053 0 1.213 1.213 0 01-2.128-.04l-.083-.158a.656.656 0 00-1.002-.2l-.46.386c-.804.674-2.014-.006-1.856-1.044l1.143-7.498zm4.374.539c0 .773-.553 1.4-1.235 1.4-.683 0-1.236-.627-1.236-1.4 0-.773.553-1.4 1.236-1.4.682 0 1.235.627 1.235 1.4zm2.314 1.4c.683 0 1.236-.627 1.236-1.4 0-.773-.553-1.4-1.236-1.4-.682 0-1.235.627-1.235 1.4 0 .773.553 1.4 1.235 1.4z"
            fill="#E0903D"
            fillRule="evenodd"
          />
        </g>
      </g>
      <defs>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="70"
          id={`${id}a`}
          width="56"
          x="0"
          y="0"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 1 0 0 0 0 0.372549 0 0 0 0 0.427451 0 0 0 1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_15396_130670"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_15396_130670"
            result="shape"
          />
        </filter>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="13.386"
          id={`${id}c`}
          width="12.14"
          x="22"
          y="21.28"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130670" />
        </filter>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="21"
          x2="1.615"
          y1="24.5"
          y2="37.423"
        >
          <stop stopColor="#FFC371" />
          <stop offset="1" stopColor="#FF5F6D" />
        </linearGradient>
      </defs>
    </svg>
  );
}

export function HauntsIcon({
  className,
  state = "normal",
  ...props
}: PinIconProps) {
  const id = useId();
  const [fill, startColor, endColor] = useMemo(
    () => PIN_STATE_COLORS[state],
    [state]
  );

  return (
    <svg
      aria-hidden="true"
      className={cn("h-10.5 w-7", className)}
      fill="none"
      viewBox="0 0 28 42"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M14 0C6.268 0 0 6.268 0 14c0 2.688.758 5.2 2.071 7.332L14 42l11.998-20.78A13.935 13.935 0 0028 14c0-7.732-6.268-14-14-14z"
        fill={`url(#${id}a)`}
        fillRule="evenodd"
      />
      <g filter={`url(#${id}b)`}>
        <path
          clipRule="evenodd"
          d="M9.157 11.501a4.97 4.97 0 019.826 0L20.126 19c.158 1.037-1.053 1.717-1.857 1.043l-.46-.385a.655.655 0 00-1.002.199l-.082.158a1.213 1.213 0 01-2.129.04.607.607 0 00-1.053 0 1.213 1.213 0 01-2.128-.04l-.083-.158a.656.656 0 00-1.002-.2l-.46.386c-.804.674-2.014-.006-1.856-1.043l1.143-7.5zm4.374.539c0 .773-.553 1.4-1.235 1.4s-1.236-.627-1.236-1.4c0-.773.553-1.4 1.236-1.4.682 0 1.235.627 1.235 1.4zm2.314 1.4c.682 0 1.236-.627 1.236-1.4 0-.773-.553-1.4-1.236-1.4-.682 0-1.235.627-1.235 1.4 0 .773.553 1.4 1.235 1.4z"
          fill={fill}
          fillRule="evenodd"
        />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}a`}
          x1="7"
          x2="-12.385"
          y1="10.5"
          y2="23.423"
        >
          <stop stopColor={startColor} />
          <stop offset="1" stopColor={endColor} />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="13.386"
          id={`${id}b`}
          width="12.14"
          x="8"
          y="7.28"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="1.5" />
          <feComposite in2="hardAlpha" k2="-1" k3="1" operator="arithmetic" />
          <feColorMatrix values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0" />
          <feBlend in2="shape" result="effect1_innerShadow_15396_130645" />
        </filter>
      </defs>
    </svg>
  );
}

export function MarkerActiveIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-[71px] w-[57px]", className)}
      fill="none"
      viewBox="0 0 57 71"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g filter={`url(#${id}a)`}>
        <path
          clipRule="evenodd"
          d="M28.05 14.235c-7.733 0-14 6.268-14 14 0 2.666.744 5.157 2.037 7.278l11.96 20.723 11.96-20.715a13.935 13.935 0 002.042-7.286c0-7.732-6.268-14-14-14z"
          fill={`url(#${id}b)`}
          fillRule="evenodd"
        />
        <circle cx="28.049" cy="28.235" fill="#E0903D" r="6" />
      </g>
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={`${id}b`}
          x1="21.049"
          x2="1.664"
          y1="24.735"
          y2="37.658"
        >
          <stop stopColor="#FFC371" />
          <stop offset="1" stopColor="#FF5F6D" />
        </linearGradient>
        <filter
          colorInterpolationFilters="sRGB"
          filterUnits="userSpaceOnUse"
          height="70"
          id={`${id}a`}
          width="56"
          x="0.049"
          y="0.235"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            result="hardAlpha"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
          />
          <feOffset />
          <feGaussianBlur stdDeviation="7" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix values="0 0 0 0 1 0 0 0 0 0.372549 0 0 0 0 0.427451 0 0 0 1 0" />
          <feBlend
            in2="BackgroundImageFix"
            result="effect1_dropShadow_16547_30991"
          />
          <feBlend
            in="SourceGraphic"
            in2="effect1_dropShadow_16547_30991"
            result="shape"
          />
        </filter>
      </defs>
    </svg>
  );
}

export function MarkerIcon({
  className,
  ...props
}: React.ComponentProps<"svg">) {
  const id = useId();

  return (
    <svg
      aria-hidden="true"
      className={cn("h-[43px] w-[29px]", className)}
      fill="none"
      viewBox="0 0 29 43"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        clipRule="evenodd"
        d="M14.5.387c-7.732 0-14 6.268-14 14 0 2.693.76 5.21 2.079 7.344L14.5 42.387l12.01-20.8a13.935 13.935 0 001.99-7.2c0-7.732-6.268-14-14-14z"
        fill={`url(#${id})`}
        fillRule="evenodd"
      />
      <circle cx="14.5" cy="14.387" fill="#90278E" r="6" />
      <defs>
        <linearGradient
          gradientUnits="userSpaceOnUse"
          id={id}
          x1="7.5"
          x2="-11.885"
          y1="10.887"
          y2="23.81"
        >
          <stop stopColor="#BD2885" />
          <stop offset="1" stopColor="#90278E" />
        </linearGradient>
      </defs>
    </svg>
  );
}
